export type Language = 'en' | 'fr' | 'es';

export interface NotFoundTranslations {
  title: string;
  description: string;
  goHome: string;
  goBack: string;
  helpfulLinks: string;
  pdfEditor: string;
  github: string;
  contact: string;
}

export interface TranslationStrings {
  [key: string]: string | NotFoundTranslations;
  notFound: NotFoundTranslations;
}

export const translations: Record<Language, TranslationStrings> = {
  en: {
    // App Header
    'app.title': 'PDF Form Editor',
    'app.description': 'Create interactive PDF forms online',
    
    // PDF Editor Page
    'editor.title': 'PDF Form Editor',
    'editor.subtitle': 'Upload a PDF and add interactive form fields',
    'editor.upload.button': 'Upload PDF',
    'editor.upload.drag': 'Drag PDF here or click to select',
    'editor.upload.accept': 'Only PDF files are accepted',
    'editor.drawing.mode': 'Drawing Mode',
    'editor.drawing.text': 'Text Field',
    'editor.drawing.checkbox': 'Checkbox',
    'editor.download.button': 'Download PDF with Forms',
    'editor.clear.button': 'Clear All Fields',
    'editor.instructions': 'How to use',
    'editor.instructions.detailed': '1. Upload a PDF file\n2. Select field type (text or checkbox)\n3. Click and drag on the PDF to draw field area\n4. Give the field a name\n5. Save the PDF with embedded fields',
    'editor.instructions.step1': 'Upload a PDF file',
    'editor.instructions.step2': 'Select field type (text or checkbox)',
    'editor.instructions.step3': 'Click and drag on the PDF to draw field area',
    'editor.instructions.step4': 'Give the field a name',
    'editor.instructions.step5': 'Save the PDF with embedded fields',
    'editor.instructions.show': 'Show Instructions',
    'editor.instructions.hide': 'Hide',
    'editor.upload.success': 'PDF uploaded successfully',
    'editor.upload.change': 'Change PDF',
    'editor.field.name': 'Field Name',
    'editor.field.type': 'Field Type',
    'editor.field.page': 'Page',
    'editor.field.delete': 'Delete Field',
    'editor.field.existing': 'Existing Field',
    'editor.field.new': 'New Field',
    
    // PDF Editor UI
    'editor.save.button': 'Save PDF with Fields',
    'editor.save.button.short': 'Save',
    'editor.fields.show': 'Fields',
    'editor.fields.hide': 'Hide',
    'editor.field.type.label': 'Field Type',
    'editor.field.type.text': '📝 Text',
    'editor.field.type.checkbox': '☑️ Checkbox',
    'editor.field.fontSize.label': 'Font Size',
    'editor.field.checkboxSize.label': 'Checkbox Size',
    'editor.page.label': 'Page:',
    'editor.page.previous': 'Previous',
    'editor.page.next': 'Next',
    'editor.fields.title': 'Defined Fields',
    'editor.fields.new': 'New Fields',
    'editor.fields.existing': 'Existing Fields',
    'editor.fields.empty': 'No fields defined. Draw areas on the PDF to create fields.',
    'editor.fields.search.placeholder': 'Search for a field...',
    'editor.fields.search.noresults': 'No fields found for this search',
    'editor.field.type.text.short': 'Text',
    'editor.field.type.checkbox.short': 'Checkbox',
    'editor.field.type.image': '🖼️ Image',
    'editor.field.type.image.short': 'Image',
    'editor.field.existing.badge': 'Existing',
    'editor.image.add': 'Add image',
    'editor.image.select': 'Select image',
    'editor.image.added': 'Image added',
    'editor.image.unnamed': 'Unnamed image',
    'editor.placeholder.font': 'Font',
    'editor.placeholder.size': 'Size',
    'editor.placeholder.fontsize': 'Font Size',
    
    // Modal
    'modal.field.title': 'Configure Field',
    'modal.field.title.edit': 'Edit Field',
    'modal.field.name.label': 'Field Name',
    'modal.field.name.placeholder': 'Enter field name',
    'modal.field.type.label': 'Type:',
    'modal.field.type.text.label': 'Text Field',
    'modal.field.type.checkbox.label': 'Checkbox',
    'modal.field.position.label': 'Position:',
    'modal.field.size.label': 'Size:',
    'modal.cancel': 'Cancel',
    'modal.save': 'Save',
    'editor.field.edit': 'Edit Field',
    
    // Language Selector
    'language.english': 'English',
    'language.french': 'Français',
    'language.spanish': 'Español',
    'language.select': 'Select Language',
    
    // Messages
    'message.upload.success': 'PDF uploaded successfully',
    'message.upload.error': 'Error uploading PDF',
    'message.download.success': 'PDF downloaded successfully',
    'message.download.error': 'Error downloading PDF',
    'message.field.created': 'Field created successfully',
    'message.field.deleted': 'Field deleted successfully',
    'message.fields.cleared': 'All fields cleared',
    'message.pdf.saved': 'PDF saved successfully',
    'message.pdf.saved.detail': 'PDF with {count} fields has been downloaded.',
    'message.pdf.save.error': 'Error saving PDF',
    'message.pdf.save.error.detail': 'Unable to save PDF. Please try again.',
    
    // SEO
    'seo.keywords': 'pdf editor, pdf form creator, interactive pdf, pdf form fields, online pdf editor',
    
    // Social Sharing
    'social.share.twitter': 'Share on Twitter',
    'social.share.facebook': 'Share on Facebook',
    'social.share.linkedin': 'Share on LinkedIn',
    'social.share.copy': 'Copy Link',
    'social.platform.twitter': 'Twitter',
    'social.platform.facebook': 'Facebook',
    'social.platform.linkedin': 'LinkedIn',
    
    // Footer
    'footer.privacy': 'Privacy: We do not store any data. All PDFs are processed locally on your device.',
    'footer.contact': 'Contact: contact@form-pdf.com',
    
    // Projects Management
    'projects.modal.title': 'Saved PDF Projects',
    'projects.button': 'Projects',
    'projects.save.button': 'Save',
    'projects.save.modal.title': 'Save Project',
    'projects.save.name.label': 'Project Name',
    'projects.save.name.placeholder': 'Enter project name',
    'projects.save.name.exists': 'A project with this name already exists. It will be updated.',
    'projects.save.button.confirm': 'Save Project',
    'projects.save.success': 'Project saved',
    'projects.save.success.detail': 'Project has been saved successfully',
    'projects.save.error': 'Error',
    'projects.save.error.detail': 'Unable to save project',
    'projects.list.empty': 'No saved projects',
    'projects.list.empty.detail': 'Save your PDFs with their fields to find them later',
    'projects.list.search.placeholder': 'Search for a project...',
    'projects.list.search.noresults': 'No results',
    'projects.list.search.noresults.detail': 'No project matches your search',
    'projects.count': '{count} project{plural} - {size}',
    'projects.deleteall.button': 'Delete All',
    'projects.delete.success': 'Project deleted',
    'projects.delete.success.detail': 'The PDF project has been deleted successfully',
    'projects.delete.error': 'Error',
    'projects.delete.error.detail': 'Unable to delete project',
    'projects.deleteall.success': 'All projects deleted',
    'projects.deleteall.success.detail': 'All PDF projects have been deleted',
    'projects.deleteall.error': 'Error',
    'projects.deleteall.error.detail': 'Unable to delete all projects',
    'projects.load.error': 'Error',
    'projects.load.error.detail': 'Unable to load project',
    'projects.load.tooltip': 'Load this project',
    'projects.delete.tooltip': 'Delete this project',
    'projects.pages': '{count} page{plural}',
    'projects.fields': '{count} field{plural}',
    'projects.modified': 'Modified on {date}',
    'projects.save.name.required': 'Project name is required',
    'projects.save.name.toolong': 'Project name cannot exceed 100 characters',
    'projects.save.name.exists.error': 'A project with this name already exists',
    'projects.save.nopdf': 'No PDF loaded',
    'projects.save.storage.full': 'Insufficient storage space',
    'projects.save.storage.full.detail': 'Delete some projects to free up space',
    'projects.save.success.detail.full': 'Project "{name}" has been saved successfully',
    
    // 404 Not Found Page
    notFound: {
      title: 'Page Not Found',
      description: 'The page you are looking for does not exist. It might have been moved, deleted, or you entered the wrong URL.',
      goHome: 'Go Home',
      goBack: 'Go Back',
      helpfulLinks: 'Helpful Links:',
      pdfEditor: 'PDF Editor',
      github: 'GitHub',
      contact: 'If you think this is a mistake, please contact us.'
    }
  },
  fr: {
    // App Header
    'app.title': 'Éditeur de Formulaires PDF',
    'app.description': 'Créez des formulaires PDF interactifs en ligne',
    
    // PDF Editor Page
    'editor.title': 'Éditeur de Formulaires PDF',
    'editor.subtitle': 'Téléchargez un PDF et ajoutez des champs de formulaire interactifs',
    'editor.upload.button': 'Télécharger PDF',
    'editor.upload.drag': 'Glissez le PDF ici ou cliquez pour sélectionner',
    'editor.upload.accept': 'Seuls les fichiers PDF sont acceptés',
    'editor.drawing.mode': 'Mode de Dessin',
    'editor.drawing.text': 'Champ Texte',
    'editor.drawing.checkbox': 'Case à Cocher',
    'editor.download.button': 'Télécharger PDF avec Formulaires',
    'editor.clear.button': 'Effacer Tous les Champs',
    'editor.instructions': 'Comment utiliser',
    'editor.instructions.detailed': '1. Téléchargez un fichier PDF\n2. Sélectionnez le type de champ (texte ou case à cocher)\n3. Cliquez et glissez sur le PDF pour dessiner la zone du champ\n4. Donnez un nom au champ\n5. Sauvegardez le PDF avec les champs intégrés',
    'editor.instructions.step1': 'Téléchargez un fichier PDF',
    'editor.instructions.step2': 'Sélectionnez le type de champ (texte ou case à cocher)',
    'editor.instructions.step3': 'Cliquez et glissez sur le PDF pour dessiner la zone du champ',
    'editor.instructions.step4': 'Donnez un nom au champ',
    'editor.instructions.step5': 'Sauvegardez le PDF avec les champs intégrés',
    'editor.instructions.show': 'Afficher les Instructions',
    'editor.instructions.hide': 'Masquer',
    'editor.upload.success': 'PDF téléchargé avec succès',
    'editor.upload.change': 'Changer de PDF',
    'editor.field.name': 'Nom du Champ',
    'editor.field.type': 'Type de Champ',
    'editor.field.page': 'Page',
    'editor.field.delete': 'Supprimer le Champ',
    'editor.field.existing': 'Champ Existant',
    'editor.field.new': 'Nouveau Champ',
    
    // PDF Editor UI
    'editor.save.button': 'Sauvegarder PDF avec champs',
    'editor.save.button.short': 'Sauvegarder',
    'editor.fields.show': 'Champs',
    'editor.fields.hide': 'Masquer',
    'editor.field.type.label': 'Type de champ',
    'editor.field.type.text': '📝 Texte',
    'editor.field.type.checkbox': '☑️ Case à cocher',
    'editor.field.fontSize.label': 'Taille de police',
    'editor.page.label': 'Page:',
    'editor.page.previous': 'Précédent',
    'editor.page.next': 'Suivant',
    'editor.fields.title': 'Champs définis',
    'editor.fields.new': 'Nouveaux champs',
    'editor.fields.existing': 'Champs existants',
    'editor.fields.empty': 'Aucun champ défini. Dessinez des zones sur le PDF pour créer des champs.',
    'editor.fields.search.placeholder': 'Rechercher un champ...',
    'editor.fields.search.noresults': 'Aucun champ trouvé pour cette recherche',
    'editor.field.type.text.short': 'Texte',
    'editor.field.type.checkbox.short': 'Case à cocher',
    'editor.field.type.image': '🖼️ Image',
    'editor.field.type.image.short': 'Image',
    'editor.field.existing.badge': 'Existant',
    'editor.image.add': 'Ajouter une image',
    'editor.image.select': 'Sélectionner image',
    'editor.image.added': 'Image ajoutée',
    'editor.image.unnamed': 'Image sans nom',
    'editor.placeholder.font': 'Police',
    'editor.placeholder.size': 'Taille',
    'editor.placeholder.fontsize': 'Taille de police',
    
    // Modal
    'modal.field.title': 'Configurer le champ',
    'modal.field.title.edit': 'Modifier le champ',
    'modal.field.name.label': 'Nom du champ',
    'modal.field.name.placeholder': 'Entrez le nom du champ',
    'modal.field.type.label': 'Type:',
    'modal.field.type.text.label': 'Champ texte',
    'modal.field.type.checkbox.label': 'Case à cocher',
    'modal.field.position.label': 'Position:',
    'modal.field.size.label': 'Taille:',
    'modal.cancel': 'Annuler',
    'modal.save': 'Sauvegarder',
    'editor.field.edit': 'Modifier le champ',
    
    // Language Selector
    'language.english': 'English',
    'language.french': 'Français',
    'language.spanish': 'Español',
    'language.select': 'Sélectionner la Langue',
    
    // Messages
    'message.upload.success': 'PDF téléchargé avec succès',
    'message.upload.error': 'Erreur lors du téléchargement du PDF',
    'message.download.success': 'PDF téléchargé avec succès',
    'message.download.error': 'Erreur lors du téléchargement du PDF',
    'message.field.created': 'Champ créé avec succès',
    'message.field.deleted': 'Champ supprimé avec succès',
    'message.fields.cleared': 'Tous les champs effacés',
    'message.pdf.saved': 'PDF sauvegardé avec succès',
    'message.pdf.saved.detail': 'Le PDF avec {count} champs a été téléchargé.',
    'message.pdf.save.error': 'Erreur lors de la sauvegarde',
    'message.pdf.save.error.detail': 'Impossible de sauvegarder le PDF. Veuillez réessayer.',
    
    // SEO
    'seo.keywords': 'éditeur pdf, créateur de formulaires pdf, pdf interactif, champs de formulaire pdf, éditeur pdf en ligne',
    
    // Social Sharing
    'social.share.twitter': 'Partager sur Twitter',
    'social.share.facebook': 'Partager sur Facebook',
    'social.share.linkedin': 'Partager sur LinkedIn',
    'social.share.copy': 'Copier le lien',
    'social.platform.twitter': 'Twitter',
    'social.platform.facebook': 'Facebook',
    'social.platform.linkedin': 'LinkedIn',
    
    // Footer
    'footer.privacy': 'Confidentialité : Nous ne stockons aucune donnée. Tous les PDF sont traités localement sur votre appareil.',
    'footer.contact': 'Contact : contact@form-pdf.com',
    
    // Projects Management
    'projects.modal.title': 'Projets PDF sauvegardés',
    'projects.button': 'Projets',
    'projects.save.button': 'Sauvegarder',
    'projects.save.modal.title': 'Sauvegarder le projet',
    'projects.save.name.label': 'Nom du projet',
    'projects.save.name.placeholder': 'Entrez le nom du projet',
    'projects.save.name.exists': 'Un projet avec ce nom existe déjà. Il sera mis à jour.',
    'projects.save.button.confirm': 'Sauvegarder le projet',
    'projects.save.success': 'Projet sauvegardé',
    'projects.save.success.detail': 'Le projet a été sauvegardé avec succès',
    'projects.save.error': 'Erreur',
    'projects.save.error.detail': 'Impossible de sauvegarder le projet',
    'projects.list.empty': 'Aucun projet sauvegardé',
    'projects.list.empty.detail': 'Sauvegardez vos PDFs avec leurs champs pour les retrouver plus tard',
    'projects.list.search.placeholder': 'Rechercher un projet...',
    'projects.list.search.noresults': 'Aucun résultat',
    'projects.list.search.noresults.detail': 'Aucun projet ne correspond à votre recherche',
    'projects.count': '{count} projet{plural} - {size}',
    'projects.deleteall.button': 'Tout supprimer',
    'projects.delete.success': 'Projet supprimé',
    'projects.delete.success.detail': 'Le projet PDF a été supprimé avec succès',
    'projects.delete.error': 'Erreur',
    'projects.delete.error.detail': 'Impossible de supprimer le projet',
    'projects.deleteall.success': 'Tous les projets supprimés',
    'projects.deleteall.success.detail': 'Tous les projets PDF ont été supprimés',
    'projects.deleteall.error': 'Erreur',
    'projects.deleteall.error.detail': 'Impossible de supprimer tous les projets',
    'projects.load.error': 'Erreur',
    'projects.load.error.detail': 'Impossible de charger le projet',
    'projects.load.tooltip': 'Charger ce projet',
    'projects.delete.tooltip': 'Supprimer ce projet',
    'projects.pages': '{count} page{plural}',
    'projects.fields': '{count} champ{plural}',
    'projects.modified': 'Modifié le {date}',
    'projects.save.name.required': 'Le nom du projet est requis',
    'projects.save.name.toolong': 'Le nom du projet ne peut pas dépasser 100 caractères',
    'projects.save.name.exists.error': 'Un projet avec ce nom existe déjà',
    'projects.save.nopdf': 'Aucun PDF chargé',
    'projects.save.storage.full': 'Espace de stockage insuffisant',
    'projects.save.storage.full.detail': 'Supprimez quelques projets pour libérer de l\'espace',
    'projects.save.success.detail.full': 'Le projet "{name}" a été sauvegardé avec succès',
    
    // 404 Not Found Page
    notFound: {
      title: 'Page non trouvée',
      description: 'La page que vous recherchez n\'existe pas. Elle a peut-être été déplacée, supprimée, ou vous avez entré une URL incorrecte.',
      goHome: 'Retour à l\'accueil',
      goBack: 'Retour',
      helpfulLinks: 'Liens utiles:',
      pdfEditor: 'Éditeur PDF',
      github: 'GitHub',
      contact: 'Si vous pensez qu\'il s\'agit d\'une erreur, veuillez nous contacter.'
    }
  },
  es: {
    // App Header
    'app.title': 'Editor de Formularios PDF',
    'app.description': 'Crea formularios PDF interactivos en línea',
    
    // PDF Editor Page
    'editor.title': 'Editor de Formularios PDF',
    'editor.subtitle': 'Sube un PDF y agrega campos de formulario interactivos',
    'editor.upload.button': 'Subir PDF',
    'editor.upload.drag': 'Arrastra el PDF aquí o haz clic para seleccionar',
    'editor.upload.accept': 'Solo se aceptan archivos PDF',
    'editor.drawing.mode': 'Modo de Dibujo',
    'editor.drawing.text': 'Campo de Texto',
    'editor.drawing.checkbox': 'Casilla de Verificación',
    'editor.download.button': 'Descargar PDF con Formularios',
    'editor.clear.button': 'Limpiar Todos los Campos',
    'editor.instructions': 'Cómo usar',
    'editor.instructions.detailed': '1. Sube un archivo PDF\n2. Selecciona el tipo de campo (texto o casilla de verificación)\n3. Haz clic y arrastra en el PDF para dibujar el área del campo\n4. Dale un nombre al campo\n5. Guarda el PDF con los campos integrados',
    'editor.instructions.step1': 'Sube un archivo PDF',
    'editor.instructions.step2': 'Selecciona el tipo de campo (texto o casilla de verificación)',
    'editor.instructions.step3': 'Haz clic y arrastra en el PDF para dibujar el área del campo',
    'editor.instructions.step4': 'Dale un nombre al campo',
    'editor.instructions.step5': 'Guarda el PDF con los campos integrados',
    'editor.instructions.show': 'Mostrar Instrucciones',
    'editor.instructions.hide': 'Ocultar',
    'editor.upload.success': 'PDF subido exitosamente',
    'editor.upload.change': 'Cambiar PDF',
    'editor.field.name': 'Nombre del Campo',
    'editor.field.type': 'Tipo de Campo',
    'editor.field.page': 'Página',
    'editor.field.delete': 'Eliminar Campo',
    'editor.field.existing': 'Campo Existente',
    'editor.field.new': 'Nuevo Campo',
    
    // PDF Editor UI
    'editor.save.button': 'Guardar PDF con Campos',
    'editor.save.button.short': 'Guardar',
    'editor.fields.show': 'Campos',
    'editor.fields.hide': 'Ocultar',
    'editor.field.type.label': 'Tipo de Campo',
    'editor.field.type.text': '📝 Texto',
    'editor.field.type.checkbox': '☑️ Casilla de Verificación',
    'editor.field.fontSize.label': 'Tamaño de Fuente',
    'editor.page.label': 'Página:',
    'editor.page.previous': 'Anterior',
    'editor.page.next': 'Siguiente',
    'editor.fields.title': 'Campos Definidos',
    'editor.fields.new': 'Nuevos Campos',
    'editor.fields.existing': 'Campos Existentes',
    'editor.fields.empty': 'No hay campos definidos. Dibuja áreas en el PDF para crear campos.',
    'editor.fields.search.placeholder': 'Buscar un campo...',
    'editor.fields.search.noresults': 'No se encontraron campos para esta búsqueda',
    'editor.field.type.text.short': 'Texto',
    'editor.field.type.checkbox.short': 'Casilla de Verificación',
    'editor.field.type.image': '🖼️ Imagen',
    'editor.field.type.image.short': 'Imagen',
    'editor.field.existing.badge': 'Existente',
    'editor.image.add': 'Añadir imagen',
    'editor.image.select': 'Seleccionar imagen',
    'editor.image.added': 'Imagen añadida',
    'editor.image.unnamed': 'Imagen sin nombre',
    'editor.placeholder.font': 'Fuente',
    'editor.placeholder.size': 'Tamaño',
    'editor.placeholder.fontsize': 'Tamaño de fuente',
    
    // Modal
    'modal.field.title': 'Configurar Campo',
    'modal.field.title.edit': 'Editar Campo',
    'modal.field.name.label': 'Nombre del Campo',
    'modal.field.name.placeholder': 'Ingresa el nombre del campo',
    'modal.field.type.label': 'Tipo:',
    'modal.field.type.text.label': 'Campo de Texto',
    'modal.field.type.checkbox.label': 'Casilla de Verificación',
    'modal.field.position.label': 'Posición:',
    'modal.field.size.label': 'Tamaño:',
    'modal.cancel': 'Cancelar',
    'modal.save': 'Guardar',
    'editor.field.edit': 'Editar Campo',
    
    // Language Selector
    'language.english': 'English',
    'language.french': 'Français',
    'language.spanish': 'Español',
    'language.select': 'Seleccionar Idioma',
    
    // Messages
    'message.upload.success': 'PDF subido exitosamente',
    'message.upload.error': 'Error al subir el PDF',
    'message.download.success': 'PDF descargado exitosamente',
    'message.download.error': 'Error al descargar el PDF',
    'message.field.created': 'Campo creado exitosamente',
    'message.field.deleted': 'Campo eliminado exitosamente',
    'message.fields.cleared': 'Todos los campos eliminados',
    'message.pdf.saved': 'PDF guardado exitosamente',
    'message.pdf.saved.detail': 'PDF con {count} campos ha sido descargado.',
    'message.pdf.save.error': 'Error al guardar PDF',
    'message.pdf.save.error.detail': 'No se pudo guardar el PDF. Por favor intenta de nuevo.',
    
    // SEO
    'seo.keywords': 'editor pdf, creador de formularios pdf, pdf interactivo, campos de formulario pdf, editor pdf en línea',
    
    // Social Sharing
    'social.share.twitter': 'Compartir en Twitter',
    'social.share.facebook': 'Compartir en Facebook',
    'social.share.linkedin': 'Compartir en LinkedIn',
    'social.share.copy': 'Copiar enlace',
    'social.platform.twitter': 'Twitter',
    'social.platform.facebook': 'Facebook',
    'social.platform.linkedin': 'LinkedIn',
    
    // Footer
    'footer.privacy': 'Privacidad: No almacenamos ningún dato. Todos los PDFs se procesan localmente en tu dispositivo.',
    'footer.contact': 'Contacto: contact@form-pdf.com',
    
    // Projects Management
    'projects.modal.title': 'Proyectos PDF guardados',
    'projects.button': 'Proyectos',
    'projects.save.button': 'Guardar',
    'projects.save.modal.title': 'Guardar proyecto',
    'projects.save.name.label': 'Nombre del proyecto',
    'projects.save.name.placeholder': 'Ingresa el nombre del proyecto',
    'projects.save.name.exists': 'Ya existe un proyecto con este nombre. Será actualizado.',
    'projects.save.button.confirm': 'Guardar proyecto',
    'projects.save.success': 'Proyecto guardado',
    'projects.save.success.detail': 'El proyecto ha sido guardado exitosamente',
    'projects.save.error': 'Error',
    'projects.save.error.detail': 'No se pudo guardar el proyecto',
    'projects.list.empty': 'No hay proyectos guardados',
    'projects.list.empty.detail': 'Guarda tus PDFs con sus campos para encontrarlos más tarde',
    'projects.list.search.placeholder': 'Buscar un proyecto...',
    'projects.list.search.noresults': 'No hay resultados',
    'projects.list.search.noresults.detail': 'Ningún proyecto coincide con tu búsqueda',
    'projects.count': '{count} proyecto{plural} - {size}',
    'projects.deleteall.button': 'Eliminar todo',
    'projects.delete.success': 'Proyecto eliminado',
    'projects.delete.success.detail': 'El proyecto PDF ha sido eliminado exitosamente',
    'projects.delete.error': 'Error',
    'projects.delete.error.detail': 'No se pudo eliminar el proyecto',
    'projects.deleteall.success': 'Todos los proyectos eliminados',
    'projects.deleteall.success.detail': 'Todos los proyectos PDF han sido eliminados',
    'projects.deleteall.error': 'Error',
    'projects.deleteall.error.detail': 'No se pudo eliminar todos los proyectos',
    'projects.load.error': 'Error',
    'projects.load.error.detail': 'No se pudo cargar el proyecto',
    'projects.load.tooltip': 'Cargar este proyecto',
    'projects.delete.tooltip': 'Eliminar este proyecto',
    'projects.pages': '{count} página{plural}',
    'projects.fields': '{count} campo{plural}',
    'projects.modified': 'Modificado el {date}',
    'projects.save.name.required': 'El nombre del proyecto es requerido',
    'projects.save.name.toolong': 'El nombre del proyecto no puede exceder 100 caracteres',
    'projects.save.name.exists.error': 'Ya existe un proyecto con este nombre',
    'projects.save.nopdf': 'No hay PDF cargado',
    'projects.save.storage.full': 'Espacio de almacenamiento insuficiente',
    'projects.save.storage.full.detail': 'Elimina algunos proyectos para liberar espacio',
    'projects.save.success.detail.full': 'El proyecto "{name}" ha sido guardado exitosamente',
    
    // 404 Not Found Page
    notFound: {
      title: 'Página no encontrada',
      description: 'La página que estás buscando no existe. Podría haber sido movida, eliminada, o ingresaste una URL incorrecta.',
      goHome: 'Ir al inicio',
      goBack: 'Volver',
      helpfulLinks: 'Enlaces útiles:',
      pdfEditor: 'Editor PDF',
      github: 'GitHub',
      contact: 'Si crees que esto es un error, por favor contáctanos.'
    }
  }
};